import { useState, FormEvent, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

export default function BuyInquiryPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    artwork_name: '',
    message: '',
    additional_info: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemaData = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': '작품 구매 문의 - 온라인 미술 작품 전시 및 판매 플랫폼',
        'description': '관심 있는 미술 작품에 대해 구매 문의를 남겨주세요. 작품 가격, 배송, 결제 방식 등에 대해 빠르게 답변드립니다.',
        'url': `${siteUrl}/buy-inquiry`,
        'inLanguage': 'ko-KR',
        'isPartOf': {
          '@type': 'WebSite',
          'url': siteUrl,
          'name': '온라인 미술 작품 전시 및 판매 플랫폼'
        },
        'breadcrumb': {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': '홈',
              'item': siteUrl
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': '작품 구매 문의',
              'item': `${siteUrl}/buy-inquiry`
            }
          ]
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': '작품 구매 문의',
        'description': '미술품 구매를 원하시는 분들을 위한 구매 문의 페이지입니다. 관심 작품명과 문의 내용을 입력하시면 아트 딜러가 빠르게 답변드립니다.',
        'url': `${siteUrl}/buy-inquiry`
      }
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    script.id = 'buy-inquiry-schema';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('buy-inquiry-schema');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.artwork_name || !formData.message) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase
        .from('applications')
        .insert([
          {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            event_type: '작품 구매 문의',
            event_date: new Date().toISOString().split('T')[0],
            message: `[관심 작품] ${formData.artwork_name}\n\n[문의 내용]\n${formData.message}`,
            media_type: 'inquiry',
            additional_info: formData.additional_info || null,
            status: 'pending',
          },
        ]);

      if (error) throw error;

      // 이메일 알림 발송
      try {
        await fetch('https://hmjykajnrjbypiksedtd.supabase.co/functions/v1/send-inquiry-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'buy_inquiry',
            data: {
              name: formData.name,
              phone: formData.phone,
              email: formData.email,
              artwork_name: formData.artwork_name,
              message: formData.message,
              additional_info: formData.additional_info,
            }
          })
        });
      } catch (emailErr) {
        console.error('Email notification failed:', emailErr);
      }

      setSubmitStatus('success');
      setTimeout(() => {
        window.REACT_APP_NAVIGATE('/');
      }, 2000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50" role="main">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            aria-label="홈으로 돌아가기"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="font-medium">돌아가기</span>
          </button>
          <h1 className="text-xl font-bold text-gray-900">작품 구매 문의</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* 폼 컨테이너 */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {/* 타이틀 */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gradient-to-br from-coral-500 to-golden-500 rounded-full">
              <i className="ri-shopping-cart-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">작품 구매 문의</h2>
            <p className="text-gray-600 text-base">
              관심 있는 작품에 대해 문의해주시면 빠르게 답변드리겠습니다
            </p>
          </div>

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 문의자 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b border-gray-200">
                문의자 정보
              </h3>
              
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  이름 <span className="text-coral-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-base"
                  placeholder="이름을 입력하세요"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  연락처 <span className="text-coral-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-base"
                  placeholder="010-0000-0000"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  이메일 <span className="text-coral-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-base"
                  placeholder="example@email.com"
                  required
                />
              </div>
            </div>

            {/* 작품 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b border-gray-200">
                관심 작품
              </h3>
              
              <div>
                <label htmlFor="artwork_name" className="block text-sm font-medium text-gray-700 mb-2">
                  작품명 <span className="text-coral-500">*</span>
                </label>
                <input
                  type="text"
                  id="artwork_name"
                  value={formData.artwork_name}
                  onChange={(e) => setFormData({ ...formData, artwork_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-base"
                  placeholder="관심 있는 작품명을 입력하세요"
                  required
                />
              </div>
            </div>

            {/* 문의 내용 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b border-gray-200">
                문의 내용
              </h3>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  문의 내용 <span className="text-coral-500">*</span>
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-base resize-none"
                  placeholder="작품 구매에 대한 문의 사항을 자유롭게 작성해주세요 (최대 500자)"
                  required
                />
                <p className="text-sm text-gray-500 mt-2 text-right">
                  {formData.message.length} / 500자
                </p>
              </div>

              <div>
                <label htmlFor="additional_info" className="block text-sm font-medium text-gray-700 mb-2">
                  추가 요청사항
                </label>
                <textarea
                  id="additional_info"
                  value={formData.additional_info}
                  onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-base resize-none"
                  placeholder="추가로 전달하실 내용이 있다면 작성해주세요 (최대 500자)"
                />
                <p className="text-sm text-gray-500 mt-2 text-right">
                  {formData.additional_info.length} / 500자
                </p>
              </div>
            </div>

            {/* 제출 버튼 */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-coral-500 to-golden-500 text-white rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    제출 중...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2"></i>
                    문의하기
                  </>
                )}
              </button>
            </div>

            {/* 상태 메시지 */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                <p className="text-green-800 font-medium">
                  문의가 성공적으로 접수되었습니다. 곧 연락드리겠습니다.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                <p className="text-red-800 font-medium">
                  문의 접수 중 오류가 발생했습니다. 다시 시도해주세요.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </main>
  );
}